if (!customElements.get("trusted-reformer-carousel")) {
  customElements.define(
    "trusted-reformer-carousel",
    class TrustedReformerCarousel extends HTMLElement {
      connectedCallback() {
        this.sliderEl = this.querySelector(
          ".trusted-reformer-carousel__slider",
        );
        if (!this.sliderEl) return;

        this.clipEl = this.querySelector(
          ".trusted-reformer-carousel__track-clip",
        );
        this.controlsEl = this.querySelector(
          ".trusted-reformer-carousel__controls",
        );
        this.navEl = this.querySelector(".trusted-reformer-carousel__nav");
        this.prevEl = this.querySelector(".swiper-button-prev");
        this.nextEl = this.querySelector(".swiper-button-next");
        this.paginationEl = this.querySelector(".swiper-pagination");
        this._resizeObserver = null;
        this._clipResizeObserver = null;
        this._navLayoutUpdate = null;
        this._onDesktopMqNav = null;

        this.slideCount =
          this.sliderEl.querySelectorAll(".swiper-slide").length;

        this.slidesDesktop = parseInt(this.dataset.slidesDesktop || "3", 10);
        this.slidesTablet = parseInt(this.dataset.slidesTablet || "2", 10);
        this.sectionId = this.dataset.sectionId;
        /** 1920 稿：16px；由 CSS 随视口缩放，初始化后再测量 */
        this.spaceBetweenDesktop =
          parseInt(this.dataset.spaceBetween || "16", 10) || 16;
        /** 375 稿：8px */
        this.spaceBetweenMobile =
          parseInt(this.dataset.spaceBetweenMobile || "8", 10) || 8;
        this.desktopQuery = window.matchMedia("(min-width: 1024px)");

        this.sliderInstance = null;
        this.paginationButtons = [];
        this._onPrevClick = null;
        this._onNextClick = null;
        this._onTransitionEnd = null;
        this.initSlider();
      }

      initSlider() {
        if (this.sliderInstance) return;

        const hasNav =
          this.slideCount > 1 &&
          this.prevEl &&
          this.nextEl &&
          this.paginationEl;

        this.sliderOptions = {
          slidesPerView: "auto",
          spaceBetween: this.getTrcSpaceBetweenPx(),
          slidesPerGroup: 1,
          grabCursor: this.slideCount > 1,
          allowTouchMove: this.slideCount > 1,
          threshold: 5,
          resistanceRatio: 0.65,
          watchSlidesProgress: true,
          breakpoints: {
            1024: {
              slidesPerView: "auto",
              /* 与 Liquid 中 --trc-gap-desktop: min(16px, 16*100vw/1920) 一致；基准来自 data-space-between */
              spaceBetween: this.getTrcSpaceBetweenPx(),
              /* 自定义 setTranslate 需要 freeMode，避免被吸附回 Swiper 默认 snap */
              freeMode: true,
              /* 到底时避免橡皮筋再产生一小段位移 */
              resistanceRatio: 0,
            },
          },
          watchOverflow: true,
          keyboard: {
            enabled: this.slideCount > 1,
            onlyInViewport: true,
          },
          mousewheel: {
            enabled: this.slideCount > 1,
            forceToAxis: true,
          },
        };

        if (hasNav) {
          this.sliderOptions.navigation = false;
          this.sliderOptions.pagination = false;
        }

        this.sliderInstance = new window.FoxTheme.Carousel(
          this.sliderEl,
          this.sliderOptions,
          [FoxTheme.Swiper.Mousewheel],
        );
        this.sliderInstance.init();
        this.slider = this.sliderInstance?.slider;

        if (this.slider) {
          this.syncSwiperSpaceFromDom();
          this.updateDesktopEndPadding();
        }

        if (hasNav && this.slider) {
          this.setupCustomPagination();
          this.bindCustomNavigation();
          this.slider.on("slideChange", () => {
            this.syncActivePagination();
            this.updateArrowState();
          });
          this.slider.on("setTranslate", () => {
            this.syncActivePagination();
            this.updateArrowState();
          });
          this.slider.on("resize", () => {
            this.syncSwiperSpaceFromDom();
            this.updateDesktopEndPadding();
            this.syncActivePagination();
            this.updateArrowState();
          });
          this.slider.on("breakpoint", () => {
            this.syncSwiperSpaceFromDom();
            this.updateDesktopEndPadding();
            this.syncActivePagination();
            this.updateArrowState();
          });
          this.syncActivePagination();
          this.updateArrowState();
          this.setupDesktopNavLayout();
        }

        if (this.slider && this.clipEl) {
          this.setupClipResize();
        }

        if (
          typeof Shopify !== "undefined" &&
          Shopify.designMode &&
          this.sectionId
        ) {
          this._onBlockSelect = (e) => {
            if (e.detail.sectionId !== this.sectionId) return;
            const target = e.target;
            const idx = target?.dataset?.index;
            if (idx === undefined || !this.sliderInstance?.slider) return;
            const index = parseInt(idx, 10);
            const swiper = this.sliderInstance.slider;
            if (this.desktopQuery.matches) {
              const { lo, hi } = this.getTranslateBoundsFromSwiper(swiper);
              const span = Math.max(0, hi - lo);
              const n = this.slideCount;
              const offset = n <= 1 ? 0 : (index / Math.max(1, n - 1)) * span;
              const raw = hi - offset;
              const targetTranslate = this.normalizeDesktopTranslate(
                raw,
                lo,
                hi,
              );
              swiper.setTransition(300);
              swiper.setTranslate(targetTranslate);
              this.syncActivePagination();
              this.updateArrowState();
            } else {
              swiper.slideTo(index);
            }
          };
          document.addEventListener(
            "shopify:block:select",
            this._onBlockSelect,
          );
        }
      }

      setupCustomPagination() {
        if (!this.paginationEl) return;
        this.paginationEl.innerHTML = "";
        this.paginationButtons = [];

        for (let index = 0; index < this.slideCount; index += 1) {
          const bullet = document.createElement("button");
          bullet.type = "button";
          bullet.className = "swiper-pagination-bullet";
          bullet.setAttribute("aria-label", `Go to slide ${index + 1}`);
          bullet.dataset.index = String(index);
          bullet.addEventListener("click", () => {
            if (!this.slider) return;
            if (this.desktopQuery.matches) {
              const { lo, hi } = this.getTranslateBounds();
              const span = Math.max(0, hi - lo);
              const n = this.slideCount;
              const offset = n <= 1 ? 0 : (index / Math.max(1, n - 1)) * span;
              const raw = hi - offset;
              const targetTranslate = this.normalizeDesktopTranslate(
                raw,
                lo,
                hi,
              );
              this.slider.setTransition(300);
              this.slider.setTranslate(targetTranslate);
              this.syncActivePagination();
              this.updateArrowState();
            } else {
              this.slider.slideTo(index);
            }
          });
          this.paginationEl.appendChild(bullet);
          this.paginationButtons.push(bullet);
        }
      }

      bindCustomNavigation() {
        if (!this.prevEl || !this.nextEl || !this.slider) return;
        this._onPrevClick = (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.handleArrowClick(-1);
        };
        this._onNextClick = (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.handleArrowClick(1);
        };
        this.prevEl.addEventListener("click", this._onPrevClick, true);
        this.nextEl.addEventListener("click", this._onNextClick, true);
      }

      /**
       * Swiper 的 minTranslate / maxTranslate 在不同版本、RTL、overflow 下数值顺序可能相反，
       * 必须归一成 [lo, hi]（lo 为较小数、hi 为较大数），否则下一步会被错误夹到「最底」= 直接跳到最后一张。
       */
      getTranslateBoundsFromSwiper(swiper) {
        const a = swiper.minTranslate();
        const b = swiper.maxTranslate();
        return {
          lo: Math.min(a, b),
          hi: Math.max(a, b),
        };
      }

      /**
       * 与 section 内 CSS 一致：--trc-gap-mobile = min(8@375, 8*100vw/375)、--trc-gap-desktop 同理。
       * 基准取 data-space-between / data-space-between-mobile，与 Liquid 设计稿数字对齐。
       */
      getTrcSpaceBetweenPx() {
        const w = window.innerWidth || 375;
        if (this.desktopQuery.matches) {
          const b = this.spaceBetweenDesktop;
          return Math.min(b, (b * w) / 1920);
        }
        const b = this.spaceBetweenMobile;
        return Math.min(b, (b * w) / 375);
      }

      /** 将 Swiper spaceBetween 与上式对齐（不依赖 offsetLeft 测量，避免移动端与 8@375 不一致）。 */
      syncSwiperSpaceFromDom() {
        if (!this.slider?.params) return;
        const gap = this.getTrcSpaceBetweenPx();
        const prev = this.slider.params.spaceBetween;
        if (typeof prev === "number" && Math.abs(prev - gap) < 0.05) return;
        this.slider.params.spaceBetween = gap;
        this.slider.update();
      }

      /**
       * 步长改为「总可滚距离 / (slideCount-1)」，不再需要末尾补距。
       */
      updateDesktopEndPadding() {
        if (!this.slider?.params) return;
        this.slider.params.slidesOffsetAfter = 0;
        this.slider.update();
      }

      getTranslateBounds() {
        const a = this.slider.minTranslate();
        const b = this.slider.maxTranslate();
        return {
          lo: Math.min(a, b),
          hi: Math.max(a, b),
        };
      }

      /** 从起点算起的滚动距离（与 translate 方向无关，用于指示器） */
      getDesktopScrollOffset() {
        const { lo, hi } = this.getTranslateBounds();
        const t = this.slider.translate || 0;
        if (this.slider.params?.rtl) {
          return Math.max(0, t - lo);
        }
        return Math.max(0, hi - t);
      }

      /**
       * 把 translate 夹紧到 [lo,hi] 并贴边取整，减少末尾微抖。
       * 可选传入 bounds，避免在 setTranslate 过程中重复读取瞬时边界。
       */
      normalizeDesktopTranslate(raw, loIn, hiIn) {
        const lo = loIn !== undefined ? loIn : this.getTranslateBounds().lo;
        const hi = hiIn !== undefined ? hiIn : this.getTranslateBounds().hi;
        let x = Math.min(hi, Math.max(lo, raw));
        const edgeEps = 1.5;
        if (Math.abs(x - lo) < edgeEps) x = lo;
        if (Math.abs(x - hi) < edgeEps) x = hi;
        return Math.round(x);
      }

      /** 桌面端总可滚动距离（与指示器 offset 最大值一致） */
      getDesktopScrollSpan() {
        const { lo, hi } = this.getTranslateBounds();
        return Math.max(0, hi - lo);
      }

      /**
       * n 张图时，从起到尽需 (n-1) 次「下一页」，每次在 translate 上移动的绝对步长。
       */
      getDesktopArrowStep() {
        const n = this.slideCount;
        if (n <= 1) return 0;
        const span = this.getDesktopScrollSpan();
        return span / (n - 1);
      }

      handleArrowClick(direction) {
        if (!this.slider) return;

        if (!this.desktopQuery.matches) {
          if (direction > 0) this.slider.slideNext();
          else this.slider.slidePrev();
          return;
        }

        const { lo, hi } = this.getTranslateBounds();
        const step = this.getDesktopArrowStep();
        if (step <= 0) return;

        const currentTranslate = this.slider.translate || 0;
        const nextTranslate = currentTranslate - direction * step;
        const targetTranslate = this.normalizeDesktopTranslate(
          nextTranslate,
          lo,
          hi,
        );

        this.slider.setTransition(300);
        this.slider.setTranslate(targetTranslate);
        this.syncActivePagination();
        this.updateArrowState();
      }

      /** track-clip 尺寸变化时同步间距、末尾补距与（若有）第三张卡片下的导航位置 */
      setupClipResize() {
        if (!this.clipEl || this._clipResizeObserver) return;
        this._clipResizeObserver = new ResizeObserver(() => {
          requestAnimationFrame(() => {
            if (!this.slider) return;
            this.syncSwiperSpaceFromDom();
            this.updateDesktopEndPadding();
            if (typeof this._navLayoutUpdate === "function") {
              this._navLayoutUpdate();
            }
            if (this.paginationButtons?.length) {
              this.syncActivePagination();
              this.updateArrowState();
            }
          });
        });
        this._clipResizeObserver.observe(this.clipEl);
      }

      /**
       * 桌面端：用布局 offset 链测量第三张卡片左缘相对 track-clip 的距离（不受 wrapper translate 影响），
       * 写入 --trusted-reformer-nav-left，避免 rem/px 混算与 Swiper 取整误差。
       */
      setupDesktopNavLayout() {
        if (!this.navEl || !this.controlsEl || !this.clipEl || !this.sliderEl)
          return;

        const update = () => {
          if (!this.desktopQuery.matches) {
            this.style.removeProperty("--trusted-reformer-nav-left");
            return;
          }
          const slides = this.sliderEl.querySelectorAll(".swiper-slide");
          if (slides.length < 3) {
            this.style.removeProperty("--trusted-reformer-nav-left");
            return;
          }
          const third = slides[2];
          let left = 0;
          let el = third;
          while (el && el !== this.clipEl) {
            if (el === document.body || el === document.documentElement) {
              left = null;
              break;
            }
            left += el.offsetLeft;
            el = el.offsetParent;
          }
          if (left === null || el !== this.clipEl) {
            const clipRect = this.clipEl.getBoundingClientRect();
            const slideRect = third.getBoundingClientRect();
            left = slideRect.left - clipRect.left;
          }
          this.style.setProperty(
            "--trusted-reformer-nav-left",
            `${Math.round(left)}px`,
          );
        };

        this._navLayoutUpdate = update;
        this._onDesktopMqNav = () => update();

        this.desktopQuery.addEventListener("change", this._onDesktopMqNav);

        if (this.slider) {
          this.slider.on("resize", update);
          this.slider.on("breakpoint", update);
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(update);
        });
      }

      syncActivePagination() {
        if (!this.slider || !this.paginationButtons.length) return;

        let activeIndex = this.slider.activeIndex || 0;
        if (this.desktopQuery.matches) {
          const span = this.getDesktopScrollSpan();
          const offset = this.getDesktopScrollOffset();
          if (span <= 0 || this.slideCount <= 1) {
            activeIndex = 0;
          } else {
            /* 与 (n-1) 次点击均分位移一致：第 0…n-1 点对齐到 offset 0…span */
            activeIndex = Math.round((offset / span) * (this.slideCount - 1));
          }
        }

        activeIndex = Math.max(0, Math.min(this.slideCount - 1, activeIndex));
        this.paginationButtons.forEach((bullet, index) => {
          bullet.classList.toggle(
            "swiper-pagination-bullet-active",
            index === activeIndex,
          );
        });
      }

      updateArrowState() {
        if (!this.slider || !this.prevEl || !this.nextEl) return;

        let atStart;
        let atEnd;
        if (this.desktopQuery.matches) {
          const t = this.slider.translate || 0;
          const { lo, hi } = this.getTranslateBounds();
          const eps = 2;
          atStart = t >= hi - eps;
          atEnd = t <= lo + eps;
        } else {
          atStart = this.slider.isBeginning;
          atEnd = this.slider.isEnd;
        }

        /* 不用 swiper-button-disabled：主题会加灰/禁止光标，与自定义点击逻辑易冲突；仅用 aria + 本区块 CSS */
        this.prevEl.classList.remove("swiper-button-disabled");
        this.nextEl.classList.remove("swiper-button-disabled");
        this.prevEl.setAttribute("aria-disabled", atStart ? "true" : "false");
        this.nextEl.setAttribute("aria-disabled", atEnd ? "true" : "false");
      }

      disconnectedCallback() {
        if (this.desktopQuery && this._onDesktopMqNav) {
          this.desktopQuery.removeEventListener("change", this._onDesktopMqNav);
        }
        if (this._clipResizeObserver) {
          this._clipResizeObserver.disconnect();
          this._clipResizeObserver = null;
        }
        if (this.slider && this._navLayoutUpdate) {
          this.slider.off("resize", this._navLayoutUpdate);
          this.slider.off("breakpoint", this._navLayoutUpdate);
        }
        this._navLayoutUpdate = null;
        this._onDesktopMqNav = null;
        this.style.removeProperty("--trusted-reformer-nav-left");

        if (
          typeof Shopify !== "undefined" &&
          Shopify.designMode &&
          this._onBlockSelect
        ) {
          document.removeEventListener(
            "shopify:block:select",
            this._onBlockSelect,
          );
        }
        if (this.prevEl && this._onPrevClick) {
          this.prevEl.removeEventListener("click", this._onPrevClick, true);
        }
        if (this.nextEl && this._onNextClick) {
          this.nextEl.removeEventListener("click", this._onNextClick, true);
        }
        if (this.sliderInstance?.slider && this._onTransitionEnd) {
          this.sliderInstance.slider.off(
            "transitionEnd",
            this._onTransitionEnd,
          );
        }
        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = null;
        }
      }
    },
  );
}
